import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import CurrencyInput from 'react-currency-input-field';

import { editarProductoConfiguracion, obtenerProductoConfiguracion, limpiarProductoConfiguracion } from '../../../actions/configuracion';
import { buscarSucursales } from '../../../actions/sucursales';
import { buscarEmpleados } from '../../../actions/empleados';
import { editarBreadcrumb } from '../../../actions/breadcrumb';
import { CustomValueContainer } from '../../../components/react_select/ReactSelect';

const styles_select = {
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    container: (provided, state) => ({
        ...provided,
    }),
    control: (provided, state) => ({
        ...provided,
        paddingTop: '5px',
        paddingBottom: '5px',
    }),
    valueContainer: (provided, state) => ({
        ...provided,
        overflow: "visible",
        marginTop: "10px"
    }),
    placeholder: (provided, state) => ({
        ...provided,
        position: "absolute",
        top: state.hasValue || state.selectProps.inputValue ? -15 : "0%",
        transition: "top 0.1s, font-size 0.1s",
        fontSize: (state.hasValue || state.selectProps.inputValue) && 13
    })
};

const tiposProducto = [
    { value: 0, label: 'Alimentos' },
    { value: 1, label: 'Bebidas' },
    { value: 2, label: 'Extras' }
];

const tiposComision = [
    { value: 0, label: 'Cajero' },
    { value: 1, label: 'Bateador' },
    { value: 2, label: 'Promotor' }, 
    { value: 3, label: 'Freidor' }, 
    { value: 4, label: 'Vendedor ambulante' }, 
    { value: 5, label: 'Administrador' }
];

const EditarProducto = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const detalles = useSelector(state => state.configuracion.productos.detalles);
    const sucursalesListado = useSelector(state => state.sucursales.listado);
    const empleadosListado = useSelector(state => state.empleados.listado);

    const [productoData, setProductoData] = useState({
        id: null,
        nombre: null,
        descripcion: null,
        tipo: null,
        codigo: null,
        precio: null,
        precios: [],
        extras: [],
        comisiones: [],
        comisiones_empleado: []
    });

    const [tipoData, setTipoData] = useState(null);
    const [comisionesData, setComisionesData] = useState([
        { tipo: 0, tipoData: null, cantidad: null },
        { tipo: 1, tipoData: null, cantidad: null },
        { tipo: 2, tipoData: null, cantidad: null }, 
        { tipo: 3, tipoData: null, cantidad: null }, 
        { tipo: 4, tipoData: null, cantidad: null }
    ]);
    const [statusProcesar, setStatusProcesar] = useState(false);

    useEffect(() => {
        dispatch(buscarSucursales());
        dispatch(buscarEmpleados());
        return () => {
            dispatch(limpiarProductoConfiguracion());
        };
    }, []);

    useEffect(() => {
        if (id) {
            dispatch(obtenerProductoConfiguracion(id));
            dispatch(editarBreadcrumb([
                { nombre: 'Configuración', path: '/configuracion/productos', activo: false },
                { nombre: 'Productos', path: '/configuracion/productos', activo: false },
                { nombre: 'Editar producto', path: `/configuracion/productos/editar/${id}`, activo: true }
            ]));
        }
    }, [id]);

    useEffect(() => {
        if (detalles != null) {
            setProductoData({
                id: detalles._id,
                nombre: detalles.nombre ?? null,
                descripcion: detalles.descripcion ?? null,
                tipo: detalles.tipo ?? null,
                codigo: detalles.codigo ?? null,
                precio: detalles.precio ?? null,
                precios: detalles.precios ? detalles.precios.map(p => ({
                    sucursal: p.sucursal?._id ?? p.sucursal,
                    sucursalData: p.sucursal ? { value: p.sucursal._id ?? p.sucursal, label: p.sucursal.nombre ?? '' } : null,
                    precio: p.precio != null ? String(p.precio) : null
                })) : [],
                extras: detalles.extras ? [...detalles.extras] : [],
                comisiones: [],
                comisiones_empleado: detalles.comisiones_empleado ? detalles.comisiones_empleado.map(c => ({
                    empleado: c.empleado?._id ?? c.empleado,
                    empleadoData: c.empleado ? { value: c.empleado._id ?? c.empleado, label: `${c.empleado.nombre ?? ''} ${c.empleado.apellido_paterno ?? ''}`.trim() } : null,
                    cantidad: c.cantidad != null ? String(c.cantidad) : null
                })) : []
            });

            if (detalles.tipo != null) {
                const tipoEncontrado = tiposProducto.find(t => t.value === detalles.tipo);
                setTipoData(tipoEncontrado || null);
            }

            const nuevasComisiones = [
                { tipo: 0, cantidad: null },
                { tipo: 1, cantidad: null },
                { tipo: 2, cantidad: null }, 
                { tipo: 3, cantidad: null }, 
                { tipo: 4, cantidad: null }
            ];
            if (detalles.comisiones?.length) {
                detalles.comisiones.forEach(c => {
                    const idx = nuevasComisiones.findIndex(n => n.tipo === c.tipo);
                    if (idx !== -1) {
                        nuevasComisiones[idx].cantidad = c.cantidad != null ? String(c.cantidad) : null;
                    }
                });
            }
            setComisionesData(nuevasComisiones);
        }
    }, [detalles]);

    const handleInputChange = ({ target }) => {
        setProductoData({ ...productoData, [target.name]: target.value });
    };

    const handleSelectTipo = (option) => {
        setTipoData(option);
        setProductoData({ ...productoData, tipo: option?.value ?? null });
    };

    const handleNumberChange = (value, name) => {
        setProductoData({ ...productoData, [name]: value || '' });
    };

    /* Catálogo de precios */
    const sucursalesUsadas = productoData.precios.map(p => p.sucursal).filter(Boolean);

    const handleAgregarPrecio = () => {
        setProductoData({ ...productoData, precios: [...productoData.precios, { sucursal: null, sucursalData: null, precio: null }] });
    };

    const handlePrecioSucursalChange = (index, option) => {
        const nuevos = [...productoData.precios];
        nuevos[index].sucursal = option?.value ?? null;
        nuevos[index].sucursalData = option ?? null;
        setProductoData({ ...productoData, precios: nuevos });
    };

    const handlePrecioCantidadChange = (index, value) => {
        const nuevos = [...productoData.precios];
        nuevos[index].precio = value || null;
        setProductoData({ ...productoData, precios: nuevos });
    };

    const handleEliminarPrecio = (index) => {
        setProductoData({ ...productoData, precios: productoData.precios.filter((_, i) => i !== index) });
    };

    /* Extras */
    const handleAgregarExtra = () => {
        setProductoData({ ...productoData, extras: [...productoData.extras, { nombre: null, precio: null }] });
    };

    const handleExtraChange = (index, field, value) => {
        const nuevosExtras = [...productoData.extras];
        nuevosExtras[index][field] = value;
        setProductoData({ ...productoData, extras: nuevosExtras });
    };

    const handleEliminarExtra = (index) => {
        const nuevosExtras = productoData.extras.filter((_, i) => i !== index);
        setProductoData({ ...productoData, extras: nuevosExtras });
    };

    /* Comisiones por empleado */
    const empleadosUsados = productoData.comisiones_empleado.map(c => c.empleado).filter(Boolean);

    const handleAgregarComisionEmpleado = () => {
        setProductoData({ ...productoData, comisiones_empleado: [...productoData.comisiones_empleado, { empleado: null, empleadoData: null, cantidad: null }] });
    };

    const handleComisionEmpleadoChange = (index, option) => {
        const nuevas = [...productoData.comisiones_empleado];
        nuevas[index].empleado = option?.value ?? null;
        nuevas[index].empleadoData = option ?? null;
        setProductoData({ ...productoData, comisiones_empleado: nuevas });
    };

    const handleComisionEmpleadoCantidadChange = (index, value) => {
        const nuevas = [...productoData.comisiones_empleado];
        nuevas[index].cantidad = value || null;
        setProductoData({ ...productoData, comisiones_empleado: nuevas });
    };

    const handleEliminarComisionEmpleado = (index) => {
        setProductoData({ ...productoData, comisiones_empleado: productoData.comisiones_empleado.filter((_, i) => i !== index) });
    };

    /* Comisiones por tipo */
    const handleComisionChange = (index, value) => {
        const nuevas = [...comisionesData];
        nuevas[index].cantidad = value || null;
        setComisionesData(nuevas);
    };

    const handleSubmit = async () => {
        const comisiones = comisionesData
            .filter(c => c.cantidad !== null && c.cantidad !== '')
            .map(c => ({ tipo: c.tipo, cantidad: parseFloat(c.cantidad) }));

        const precios = productoData.precios
            .filter(p => p.sucursal && p.precio)
            .map(p => ({ sucursal: p.sucursal, precio: parseFloat(p.precio) }));

        const comisiones_empleado = productoData.comisiones_empleado
            .filter(c => c.empleado && c.cantidad)
            .map(c => ({ empleado: c.empleado, cantidad: parseFloat(c.cantidad) }));

        setStatusProcesar(true);
        await dispatch(editarProductoConfiguracion({ ...productoData, comisiones, precios, comisiones_empleado }));
        setStatusProcesar(false);
        navigate('/configuracion/productos');
    };

    return (
        <div className="row mt-5">
            <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
                <h4 className="mb-4">Editar producto</h4>
            </div>

            <div className="col-12">
                <div className="accordion" id="accordionEditarProducto">

                    {/* Sección 1: Información general */}
                    <div className="accordion-item">
                        <h2 className="accordion-header" id="headingInfo">
                            <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseInfo" aria-expanded="true" aria-controls="collapseInfo">
                                Información general
                            </button>
                        </h2>
                        <div id="collapseInfo" className="accordion-collapse collapse show" aria-labelledby="headingInfo" data-bs-parent="#accordionEditarProducto">
                            <div className="accordion-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Nombre"
                                                id="nombre"
                                                name="nombre"
                                                value={productoData.nombre || ''}
                                                onChange={handleInputChange}
                                            />
                                            <label htmlFor="nombre">Nombre</label>
                                        </div>
                                    </div>
                                    <div className="col-md-2 form-group mb-3">
                                        <div className="input-group h-100">
                                            <Select
                                                name="tipo"
                                                className="select-group"
                                                classNamePrefix="react-select"
                                                placeholder="Tipo"
                                                options={tiposProducto}
                                                value={tipoData}
                                                onChange={handleSelectTipo}
                                                menuPortalTarget={document.body}
                                                components={{ ValueContainer: CustomValueContainer }}
                                                styles={{ ...styles_select }}
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-2 mb-3">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Código"
                                                id="codigo"
                                                name="codigo"
                                                value={productoData.codigo || ''}
                                                onChange={handleInputChange}
                                            />
                                            <label htmlFor="codigo">Código</label>
                                        </div>
                                    </div>
                                    <div className="col-md-2 mb-3">
                                        <div className="form-floating">
                                            <CurrencyInput
                                                className="form-control"
                                                placeholder="Precio"
                                                id="precio"
                                                name="precio"
                                                value={productoData.precio || ''}
                                                onValueChange={(value, name) => handleNumberChange(value, name)}
                                                prefix="$"
                                                decimalsLimit={2}
                                                groupSeparator=","
                                                decimalSeparator="."
                                            />
                                            <label htmlFor="precio">Precio</label>
                                        </div>
                                    </div>
                                    <div className="col-md-12 form-group mb-3">
                                        <div className="input-group">
                                            <div className="form-floating">
                                                <textarea
                                                    className="form-control textareCampo"
                                                    placeholder="Descripción"
                                                    id="descripcion"
                                                    name="descripcion"
                                                    value={productoData.descripcion || ''}
                                                    onChange={handleInputChange}
                                                ></textarea>
                                                <label htmlFor="descripcion">Descripción</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sección 2: Catálogo de precios */}
                    <div className="accordion-item">
                        <h2 className="accordion-header" id="headingPrecios">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapsePrecios" aria-expanded="false" aria-controls="collapsePrecios">
                                Catálogo de precios
                            </button>
                        </h2>
                        <div id="collapsePrecios" className="accordion-collapse collapse" aria-labelledby="headingPrecios" data-bs-parent="#accordionEditarProducto">
                            <div className="accordion-body">
                                <div className="row">
                                    {productoData.precios.map((precio, index) => {
                                        const sucursalesOpciones = (sucursalesListado || [])
                                            .filter(s => !sucursalesUsadas.includes(s._id) || s._id === precio.sucursal)
                                            .map(s => ({ value: s._id, label: s.nombre }));
                                        return (
                                            <div className="col-md-6 row align-items-center mb-2" key={index}>
                                                <div className="col-md-6 form-group mb-2 mb-md-0">
                                                    <div className="input-group h-100">
                                                        <Select
                                                            className="select-group"
                                                            classNamePrefix="react-select"
                                                            placeholder="Sucursal"
                                                            options={sucursalesOpciones}
                                                            value={precio.sucursalData}
                                                            onChange={(option) => handlePrecioSucursalChange(index, option)}
                                                            menuPortalTarget={document.body}
                                                            components={{ ValueContainer: CustomValueContainer }}
                                                            styles={{ ...styles_select }}
                                                            isClearable
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-5 mb-2 mb-md-0">
                                                    <div className="form-floating">
                                                        <CurrencyInput
                                                            className="form-control"
                                                            placeholder="Precio"
                                                            id={`precio-especial-${index}`}
                                                            value={precio.precio || ''}
                                                            onValueChange={(value) => handlePrecioCantidadChange(index, value)}
                                                            prefix="$"
                                                            decimalsLimit={2}
                                                            groupSeparator=","
                                                            decimalSeparator="."
                                                        />
                                                        <label htmlFor={`precio-especial-${index}`}>Precio</label>
                                                    </div>
                                                </div>
                                                <div className="col-md-1 d-flex justify-content-center">
                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => handleEliminarPrecio(index)}
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <button className="btn btn-outline-primary btn-sm mt-2" onClick={handleAgregarPrecio}>
                                    <i className="fas fa-plus me-1"></i> Agregar precio
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Sección 3: Extras */}
                    <div className="accordion-item">
                        <h2 className="accordion-header" id="headingExtras">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseExtras" aria-expanded="false" aria-controls="collapseExtras">
                                Extras
                            </button>
                        </h2>
                        <div id="collapseExtras" className="accordion-collapse collapse" aria-labelledby="headingExtras" data-bs-parent="#accordionEditarProducto">
                            <div className="accordion-body">
                                <div className="row">
                                    {productoData.extras.map((extra, index) => (
                                        <div className="col-md-6 row align-items-center mb-2" key={index}>
                                            <div className="col-md-6 mb-2 mb-md-0">
                                                <div className="form-floating">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Nombre del extra"
                                                        id={`extra-nombre-${index}`}
                                                        value={extra.nombre || ''}
                                                        onChange={(e) => handleExtraChange(index, 'nombre', e.target.value)}
                                                    />
                                                    <label htmlFor={`extra-nombre-${index}`}>Nombre del extra</label>
                                                </div>
                                            </div>
                                            <div className="col-md-5 mb-2 mb-md-0">
                                                <div className="form-floating">
                                                    <CurrencyInput
                                                        className="form-control"
                                                        placeholder="Precio"
                                                        id={`extra-precio-${index}`}
                                                        value={extra.precio || ''}
                                                        onValueChange={(value) => handleExtraChange(index, 'precio', value || null)}
                                                        prefix="$"
                                                        decimalsLimit={2}
                                                        groupSeparator=","
                                                        decimalSeparator="."
                                                    />
                                                    <label htmlFor={`extra-precio-${index}`}>Precio</label>
                                                </div>
                                            </div>
                                            <div className="col-md-1 d-flex justify-content-end">
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => handleEliminarExtra(index)}
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <button className="btn btn-outline-primary btn-sm mt-2" onClick={handleAgregarExtra}>
                                    <i className="fas fa-plus me-1"></i> Agregar extra
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Sección 4: Comisiones por empleado */}
                    <div className="accordion-item">
                        <h2 className="accordion-header" id="headingComisionesEmpleado">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseComisionesEmpleado" aria-expanded="false" aria-controls="collapseComisionesEmpleado">
                                Comisiones por empleado
                            </button>
                        </h2>
                        <div id="collapseComisionesEmpleado" className="accordion-collapse collapse" aria-labelledby="headingComisionesEmpleado" data-bs-parent="#accordionEditarProducto">
                            <div className="accordion-body">
                                <div className="row">
                                    {productoData.comisiones_empleado.map((ce, index) => {
                                        const empleadosOpciones = (empleadosListado || [])
                                            .filter(e => !empleadosUsados.includes(e._id) || e._id === ce.empleado)
                                            .map(e => ({ value: e._id, label: `${e.nombre} ${e.apellido_paterno}` }));
                                        return (
                                            <div className="col-md-6 row align-items-center mb-2" key={index}>
                                                <div className="col-md-6 form-group mb-2 mb-md-0">
                                                    <div className="input-group h-100">
                                                        <Select
                                                            className="select-group"
                                                            classNamePrefix="react-select"
                                                            placeholder="Empleado"
                                                            options={empleadosOpciones}
                                                            value={ce.empleadoData}
                                                            onChange={(option) => handleComisionEmpleadoChange(index, option)}
                                                            menuPortalTarget={document.body}
                                                            components={{ ValueContainer: CustomValueContainer }}
                                                            styles={{ ...styles_select }}
                                                            isClearable
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-5 mb-2 mb-md-0">
                                                    <div className="form-floating">
                                                        <CurrencyInput
                                                            className="form-control"
                                                            placeholder="Comisión"
                                                            id={`comision-emp-${index}`}
                                                            value={ce.cantidad || ''}
                                                            onValueChange={(value) => handleComisionEmpleadoCantidadChange(index, value)}
                                                            prefix="$"
                                                            decimalsLimit={2}
                                                            groupSeparator=","
                                                            decimalSeparator="."
                                                        />
                                                        <label htmlFor={`comision-emp-${index}`}>Comisión</label>
                                                    </div>
                                                </div>
                                                <div className="col-md-1 d-flex justify-content-center">
                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => handleEliminarComisionEmpleado(index)}
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <button className="btn btn-outline-primary btn-sm mt-2" onClick={handleAgregarComisionEmpleado}>
                                    <i className="fas fa-plus me-1"></i> Agregar comisión
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Sección 5: Comisiones por tipo */}
                    <div className="accordion-item">
                        <h2 className="accordion-header" id="headingComisiones">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseComisiones" aria-expanded="false" aria-controls="collapseComisiones">
                                Comisiones por tipo de empleado
                            </button>
                        </h2>
                        <div id="collapseComisiones" className="accordion-collapse collapse" aria-labelledby="headingComisiones" data-bs-parent="#accordionEditarProducto">
                            <div className="accordion-body">
                                <div className="row">
                                    {comisionesData.map((comision, index) => (
                                        <div className="col-md-2 mb-3" key={comision.tipo}>
                                            <div className="form-floating">
                                                <CurrencyInput
                                                    className="form-control"
                                                    placeholder={tiposComision[comision.tipo].label}
                                                    id={`comision-${comision.tipo}`}
                                                    value={comision.cantidad || ''}
                                                    onValueChange={(value) => handleComisionChange(index, value)}
                                                    prefix="$"
                                                    decimalsLimit={2}
                                                    groupSeparator=","
                                                    decimalSeparator="."
                                                />
                                                <label htmlFor={`comision-${comision.tipo}`}>{tiposComision[comision.tipo].label}</label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <div className="col-12 mt-3 d-flex justify-content-end">
                <button className="btn btn-secondary me-2" onClick={() => navigate('/configuracion/productos')} disabled={statusProcesar}>
                    Cancelar
                </button>
                {
                    statusProcesar
                        ? <button className="btn btn-success" disabled><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Guardando</button>
                        : <button className="btn btn-success" onClick={handleSubmit}><i className="fas fa-check me-1"></i> Guardar</button>
                }
            </div>
        </div>
    );
};

export default EditarProducto;
